import { forwardRef, TextareaHTMLAttributes, useId } from 'react';

import useAutoResizeTextarea from '@/hooks/useAutoResizeTextarea';
import cn from '@/utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  // Optional form-field affordances. When none are set, renders a bare styled
  // textarea (shadcn primitive behaviour). Height auto-grows to fit content.
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, id, className, value, style, ...props }, forwardedRef) => {
    const generatedId = useId();
    const textareaId = id ?? (label ? generatedId : undefined);
    const { ref: innerRef } = useAutoResizeTextarea(typeof value === 'string' ? value : '');

    const setRefs = (el: HTMLTextAreaElement | null): void => {
      innerRef.current = el;
      if (typeof forwardedRef === 'function') {
        forwardedRef(el);
      } else if (forwardedRef) {
        // eslint-disable-next-line no-param-reassign
        forwardedRef.current = el;
      }
    };

    const field = (
      <textarea
        ref={setRefs}
        id={textareaId}
        value={value}
        // Auto-resizing shrinks then re-measures the height on every keystroke;
        // without this, scroll-anchoring treats it as a shift and jumps the page.
        style={{ overflowAnchor: 'none', ...style }}
        className={cn(
          // 16px below `sm` so iOS Safari does not zoom the page on focus.
          'flex min-h-[72px] w-full min-w-0 rounded-md border bg-background px-3 py-2 text-base shadow-sm transition-colors sm:text-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'resize-none overflow-hidden disabled:cursor-not-allowed disabled:opacity-50',
          error ? 'border-destructive focus-visible:ring-destructive' : 'border-input',
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && textareaId ? `${textareaId}-error` : undefined}
        {...props}
      />
    );

    if (!label && !error && !helperText) return field;

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={textareaId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        {field}
        {error && (
          <p
            id={textareaId ? `${textareaId}-error` : undefined}
            className="text-xs text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && <p className="text-xs text-muted-foreground">{helperText}</p>}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
